import React, { useEffect } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { usePreloadImages } from "../hooks/usePreloadImages";

export default function AccommodationLightbox({
  isOpen,
  onClose,
  photos,
  currentIndex,
  onNavigate,
  roomName,
}) {
  // Preload selected room images for instant display in lightbox
  usePreloadImages(photos, { urlProperty: 'url' });

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          onNavigate('next');
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          onNavigate('prev');
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        default:
          // Do nothing for other keys
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isOpen, currentIndex, photos.length, onNavigate, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black border-none" hideClose>
        {photos.length > 0 && (
          <div className="relative w-full h-full">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
              onClick={onClose}
            >
              <X className="w-6 h-6" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-50 text-white hover:bg-white/20"
              onClick={() => onNavigate('prev')}
              disabled={photos.length <= 1}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-50 text-white hover:bg-white/20"
              onClick={() => onNavigate('next')}
              disabled={photos.length <= 1}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>

            <div className="relative w-full h-[88vh] flex items-center justify-center">
              <img
                src={photos[currentIndex].url}
                alt={photos[currentIndex].title || roomName}
                className="max-w-full max-h-full object-contain"
              />
            </div>

            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">{roomName}</h3>
              {photos[currentIndex].title && (
                <p className="text-base text-white/90 mb-2">{photos[currentIndex].title}</p>
              )}
              <p className="text-sm text-white/70 mt-2">
                {currentIndex + 1} of {photos.length}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
